<template>
  <div>
    <Layout ref="layoutRef" :selectedOption="1" />

    <!-- Breadcrumb -->
    <div class="mb-3 row">
      <div class="col-11">
        <nav aria-label="breadcrumb">
          <ol class="breadcrumb mb-0">
            <li class="breadcrumb-item">
              <router-link to="/tramite">Trámites</router-link>
            </li>
            <li class="breadcrumb-item">
              <router-link :to="{ name: 'TramiteForm', params: { p_idtramite } }">
                Trámite N.° {{ p_idtramite }}
              </router-link>
            </li>
            <li class="breadcrumb-item">
              <router-link 
                :to="{ name: 'TramiteForm', params: { p_idtramite }, query: { tab: 'reportes' } }"
              >
                Reportes
              </router-link>
            </li>
            <li class="breadcrumb-item active" aria-current="page">Observaciones</li>
          </ol>
        </nav>
      </div>
      <div class="col-1 text-end">
        <span 
          class="material-symbols-outlined text-primary" 
          style="font-size: 32px; cursor: pointer" 
          @click="showModalHelp = true"
          title="Ayuda"
        >
          help
        </span>
      </div>
    </div>

    <!-- Info del Trámite -->
    <div class="card border-0 mb-4" style="background-color: #e8f4fd;">
      <div class="card-body py-2 px-3">
        <span class="text-dark fw-bold">{{ datosTramite }}</span>
      </div>
    </div>

    <div class="p-4">
      <!-- Tabs -->
      <ul class="nav nav-tabs mb-4">
        <li class="nav-item" v-for="tab in tabsConfig" :key="tab.key">
          <button
            class="nav-link"
            :class="{ active: activeTab === tab.key }"
            @click="setActiveTab(tab.key)"
            type="button"
          >
            {{ tab.label }}
            <span 
              v-if="tabState(tab.key)?.loading" 
              class="spinner-border spinner-border-sm ms-2"
            ></span>
          </button>
        </li>

        <!-- Pestaña Diferencia de valores -->
        <li class="nav-item">
          <button
            class="nav-link"
            :class="{ active: activeTab === 'diferenciaValores' }"
            @click="setActiveTab('diferenciaValores')"
            type="button"
          >
            Diferencia de valores
            <span 
              v-if="tabState('diferenciaValores')?.loading" 
              class="spinner-border spinner-border-sm ms-2"
            ></span>
          </button>
        </li>

        <!-- Pestaña Diferencia de valores -->
        <li class="nav-item">
          <button
            class="nav-link"
            :class="{ active: activeTab === 'erroresCarga' }"
            @click="setActiveTab('erroresCarga')"
            type="button"
          >
            Errores de Carga
            <span 
              v-if="tabState('erroresCarga')?.loading" 
              class="spinner-border spinner-border-sm ms-2"
            ></span>
          </button>
        </li>
      </ul>

      <!-- Contenido de las pestañas -->
      <div v-for="tab in tabsConfig" :key="tab.key" v-show="activeTab === tab.key">
        <template v-if="tab.tipo === 'sinArchivos'">
          <PlanillaSinArchivo
            :data="tabState(tab.key).data"
            :loading="tabState(tab.key).loading"
            :error="tabState(tab.key).error"
            @refrescar="loadTabData(tab.key, { force: true })"
          />
        </template>

        <template v-else-if="tab.tipo === 'sinPlanillas'">
          <ArchivoSinPlanilla
            :data="tabState(tab.key).data"
            :loading="tabState(tab.key).loading"
            :error="tabState(tab.key).error"
            @refrescar="loadTabData(tab.key, { force: true })"
          />
        </template>

        <template v-else>
          <TablaResumen
            :data="tabState(tab.key).data"
            :loading="tabState(tab.key).loading"
            :error="tabState(tab.key).error"
            :tab-key="tab.key"
            :id-tramite="p_idtramite"
            @refrescar="loadTabData(tab.key, { force: true })"
          />
        </template>
      </div>

      <!-- Pestaña Diferencia de Valores -->
      <div v-if="activeTab === 'diferenciaValores'">
        <DiferenciaValores :idTramite="Number(p_idtramite)" />
      </div>

      <!-- Pestaña Diferencia de Valores -->
      <div v-if="activeTab === 'erroresCarga'">
        <ErroresCarga :idTramite="Number(p_idtramite)" />
      </div>	  
    </div>

    <Footer />
  </div>
</template>

<script>
import { ref, reactive, onMounted, watch } from "vue";
import api from "@/services/api";
import Layout from "@/components/Layout.vue";
import Footer from "@/components/Footer.vue";

import PlanillaSinArchivo from "@/views/reportes/PlanillaSinArchivo.vue";
import ArchivoSinPlanilla from "@/views/reportes/ArchivoSinPlanilla.vue";
import TablaResumen       from "@/views/reportes/TramiteResumen.vue";
import DiferenciaValores from "./DiferenciaValores.vue";
import ErroresCarga      from "./ErroresCarga.vue";

export default {
  name: "ObservacionesTabs",
  components: {
    Layout,
    Footer,
    PlanillaSinArchivo,
    ArchivoSinPlanilla,
    TablaResumen,
    DiferenciaValores,
    ErroresCarga,
  },

  props: {
    p_idtramite: { 
      type: Number, 
      required: true 
    },
  },

  setup(props) {
    const tabsConfig = [
      {
        key: "pendientes",
        label: "Pendientes",
        tipo: "default",
        endpoint: (id) => `/vwtramites/planillas-pendientes/${id}`,
      },
      {
        key: "errores",
        label: "Errores",
        tipo: "default",
        endpoint: (id) => `/db-vwplanillas-resumen/buscar-errores-tramite?idTramite=${id}`,
      },
      {
        key: "sinArchivos",
        label: "Atenciones sin archivos",
        tipo: "sinArchivos",
        endpoint: (id) => `/planilla-archivo/consultas?idTramite=${id}`,
      },
      {
        key: "sinPlanillas",
        label: "Archivos sin atenciones",
        tipo: "sinPlanillas",
        endpoint: (id) => `/archivos/pendientes-planilla/${id}`,
      },
    ];

    const activeTab = ref("pendientes");
    const showModalHelp = ref(false);
    const datosTramite = ref("");

    // Estados reactivos para cada pestaña
    const states = reactive({});

    // Inicializar estados para las pestañas principales + la adicional
    const allTabs = [...tabsConfig.map(t => t.key), "diferenciaValores"];

    allTabs.forEach((key) => {
      states[key] = {
        data: [],
        loading: false,
        error: null,
        loadedOnce: false,
      };
    });

    const tabState = (key) => states[key] || { loading: false, data: [], error: null };

    const loadTabData = async (tabKey, opts = { force: false }) => {
      if (tabKey === "diferenciaValores") return; // Esta pestaña usa componente propio

      const tabCfg = tabsConfig.find((t) => t.key === tabKey);
      if (!tabCfg) return;

      const state = states[tabKey];
      if (state.loadedOnce && !opts.force) return;

      state.loading = true;
      state.error = null;

      try {
        const url = tabCfg.endpoint(props.p_idtramite);
        const resp = await api.get(url);
        state.data = Array.isArray(resp.data) ? resp.data : [];
        state.loadedOnce = true;
      } catch (err) {
        state.error = err?.response?.data?.message || err.message || "Error al cargar datos";
        console.error(`Error cargando tab ${tabKey}:`, err);
      } finally {
        state.loading = false;
      }
    };

    const setActiveTab = (key) => {
      activeTab.value = key;
      if (key !== "diferenciaValores" && !states[key].loadedOnce) {
        loadTabData(key);
      }
    };

    const resetAndReloadAll = () => {
      Object.keys(states).forEach((key) => {
        states[key].data = [];
        states[key].loadedOnce = false;
        states[key].error = null;
      });
      loadTabData(activeTab.value, { force: true });
    };

    onMounted(() => {
      datosTramite.value = sessionStorage.getItem("datosTramite") ?? "";
      loadTabData(activeTab.value);
    });

    watch(() => props.p_idtramite, resetAndReloadAll);

    return {
      tabsConfig,
      activeTab,
      showModalHelp,
      datosTramite,
      tabState,
      setActiveTab,
      loadTabData,
    };
  },
};
</script>